import { generateMacroAnalysisMessage, MacroAnalysisMessageType, type AnalysisReply, type ChunkReceipt,
  type Lease, type MacroAnalysisMessage } from '@playcastdotio/Messaging';

type JobReply = Extract<AnalysisReply, { status?: Exclude<AnalysisReply['status'], 'capabilities'> }>;

/** Bounded descriptors only; raw pages stay in immutable object storage. */
export class AnalysisOutbox {
  private readonly acknowledged = new Map<number, string>();
  private readonly pending = new Map<number, { reply: JobReply; sentAt: number }>();
  private completion?: JobReply;
  private completionSentAt = -Infinity;
  public constructor(receipts: readonly ChunkReceipt[]) {
    for (const receipt of receipts) {
      if (this.acknowledged.has(receipt.sequence)) throw new Error('duplicate_resume_receipt');
      this.acknowledged.set(receipt.sequence, receipt.digest);
    }
  }
  public add(reply: JobReply): void {
    if (reply.chunk) {
      const chunk = reply.chunk, acknowledged = this.acknowledged.get(chunk.sequence), pending = this.pending.get(chunk.sequence);
      if ((acknowledged && acknowledged !== chunk.digest) || (pending && pending.reply.chunk!.digest !== chunk.digest)) throw new Error('conflicting_native_chunk');
      if (!acknowledged && !pending) {
        if (this.pending.size + this.acknowledged.size >= 8192) throw new Error('native_chunk_limit');
        this.pending.set(chunk.sequence, { reply, sentAt: -Infinity });
      }
    }
    if (reply.status === 'completed') {
      if (!reply.completion && !reply.previewAsset) throw new Error('native_completion_missing');
      this.completion = reply;
    }
  }
  public acknowledge(receipt: ChunkReceipt): void {
    const pending = this.pending.get(receipt.sequence), acknowledged = this.acknowledged.get(receipt.sequence);
    if (acknowledged === receipt.digest) return;
    if (!pending || pending.reply.chunk!.digest !== receipt.digest) throw new Error('invalid_chunk_acknowledgement');
    this.pending.delete(receipt.sequence); this.acknowledged.set(receipt.sequence, receipt.digest);
  }
  public messages(now: number, lease: Lease): MacroAnalysisMessage[] {
    const messages: MacroAnalysisMessage[] = [];
    for (const entry of this.pending.values()) {
      if (now - entry.sentAt < 5000) continue;
      entry.sentAt = now;
      messages.push(generateMacroAnalysisMessage({ type: MacroAnalysisMessageType.RESULT_CHUNK, requestId: entry.reply.requestId,
        reply: { ...entry.reply, lease } }));
      if (messages.length === 16) return messages;
    }
    const completion = this.completion;
    if (completion && !this.pending.size && now - this.completionSentAt >= 5000
      && (completion.previewAsset || completion.completion!.orderedChunks.every(receipt => this.acknowledged.get(receipt.sequence) === receipt.digest))) {
      this.completionSentAt = now;
      messages.push(generateMacroAnalysisMessage({ type: MacroAnalysisMessageType.COMPLETE, requestId: completion.requestId, reply: { ...completion, lease } }));
    }
    return messages;
  }
}
